import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Autocomplete, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogleDrive, faTelegram } from "@fortawesome/free-brands-svg-icons";
import {
  faSliders,
  faTrash,
  faUnlockKeyhole,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../../contexts/AuthContext";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import SheetsSettingsTab from "../../Components/Settings/sheetsSettingsTab";
import PageHeader from "../../Components/Common/PageHeader";

// Styling
import "./style.scss";
import { Button, Col, Form, InputGroup, Nav, Row, Tab } from "react-bootstrap";
import CopyButton from "../../Components/Common/CopyButton";

const DEFAULT_TAB_KEY = "google-sheet";
const TAB_TITLE_BY_KEY = {
  "google-sheet": "Google sheets",
  "telegram-report": "Telegram report",
  integration: "Integration",
};

const SettingsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTabKey, setActiveTabKey] = useState(DEFAULT_TAB_KEY);
  const [oath2Token, setOath2Token] = useState({});
  const [settings, setSettings] = useState([]);
  const [apiToken, setApiToken] = useState("");

  const [storeOptions, setStoreOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([
    { slug: 1, stores: [] },
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Utils.setPageTitle("Settings page");

    const fetchWooStores = async () => {
      setLoading(true);
      const storeRes = await Utils._apiRequest({
        path: Enums.endpoints.store.SEARCH,
        type: "GET",
      });

      setStoreOptions(
        storeRes.map((item) => ({
          label: item.storeFriendlyName,
          id: item._id,
        })),
      );
      setLoading(false);
    };

    fetchWooStores();
  }, []);

  useEffect(() => {
    const hashKey = location.hash.replace("#", "");
    const isValidHashTab = Object.prototype.hasOwnProperty.call(
      TAB_TITLE_BY_KEY,
      hashKey,
    );

    if (isValidHashTab) {
      setActiveTabKey(hashKey);
      return;
    }

    setActiveTabKey(DEFAULT_TAB_KEY);
    if (!hashKey) {
      navigate(`${location.pathname}${location.search}#${DEFAULT_TAB_KEY}`, {
        replace: true,
      });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  // Get google token from user
  useEffect(() => {
    if (currentUser?.meta?.oath2Token) {
      setOath2Token(currentUser.meta.oath2Token);
    }

    setApiToken(currentUser?.meta?.apiToken || "");
  }, [currentUser]);

  // Fetch to get settings
  useEffect(() => {
    const fetchOptions = async () => {
      const optionsRes = await Utils._apiRequest({
        path: Enums.endpoints.options.LIST,
        type: "GET",
      });

      if (optionsRes.data?.length) {
        const setttings = optionsRes.data.filter((item) => {
          return item.type === "setting";
        });
        setSettings(setttings);
      }
    };
    fetchOptions();
  }, []);

  const selectOptionHandle = (index, stores) => {
    let newData = selectedValues.slice();
    newData[index].stores = stores;
    setSelectedValues(newData);
  };

  const addMoreGroup = () => {
    let newData = selectedValues.slice();
    newData.push({ slug: newData.length, stores: [] });
    setSelectedValues(newData);
  };

  useEffect(() => {
    if (settings.length) {
      const telebot = settings.find(
        (item) => item.key === `TELEBOT-stores-${currentUser?._id}`,
      );
      if (telebot) {
        setSelectedValues(telebot.values);
      }
    }
  }, [currentUser?._id, settings]);

  const handleSaveTeleStoresOptions = async (data = selectedValues) => {
    setLoading(true);
    const payload = {
      key: `TELEBOT-stores-${currentUser?._id}`,
      group: "telebot_stores",
      values: data,
    };
    await Utils._apiRequest({
      path: Enums.endpoints.options.SAVE,
      type: "POST",
      payload: payload,
    });
    setLoading(false);
  };

  const handleUpdateSlug = (e, i) => {
    let slug = e.target.value;
    if (slug.length === 0) return false;
    slug = slug.replace(/[^a-zA-Z0-9]/g, "");
    let newData = selectedValues.slice();
    newData[i].slug = slug;
    setSelectedValues(newData);
  };

  const handleRemoveGroup = async (e, i) => {
    e.preventDefault();
    if (!window.confirm("Are you sure you want to delete this?")) {
      return false;
    }
    let newData = selectedValues.filter((_, index) => index !== i);

    await handleSaveTeleStoresOptions(newData);
    setSelectedValues(newData);
  };

  const handleApiGenerateToken = async (e) => {
    e.preventDefault();
    const integration = await Utils._apiRequest({
      path: Enums.endpoints.ext.API_TOKEN,
      type: "GET",
    });

    if (integration.token) {
      setApiToken(integration.token);
    }
  };

  const handleTabSelect = (tabKey) => {
    if (!tabKey) return;

    setActiveTabKey(tabKey);
    navigate(`${location.pathname}${location.search}#${tabKey}`, {
      replace: true,
    });
  };

  return (
    <main className="page-main">
      <div className="main-container container">
        <PageHeader 
          title="Settings Page" 
          description="Google Sheets, Telegram report, Integration" 
          icon={faSliders} 
        />
        <div className="my-3 p-3 border rounded-3 bg-body shadow-sm feature-box">
          <h6 className="heading border-bottom pb-2 mb-3">
            {TAB_TITLE_BY_KEY[activeTabKey] || TAB_TITLE_BY_KEY[DEFAULT_TAB_KEY]}
          </h6>
          <Tab.Container
            id="settings-tabs"
            activeKey={activeTabKey}
            onSelect={handleTabSelect}
          >
            <Row>
              <Col sm={3}>
                <Nav variant="pills" className="flex-column settings-tabs">
                  <Nav.Item>
                    <Nav.Link
                      eventKey="google-sheet"
                      className="settings-tab-link"
                    >
                      <FontAwesomeIcon icon={faGoogleDrive} size="lg" />
                      <div>
                        <strong className="d-block">Google sheets</strong>
                        <small>
                          <i>Manage the linked Google sheets files.</i>
                        </small>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="telegram-report"
                      className="settings-tab-link"
                    >
                      <FontAwesomeIcon icon={faTelegram} size="lg" />
                      <div>
                        <strong className="d-block">Telegram report</strong>
                        <small>
                          <i>Manage WooCommerce reports via Telegram BOT</i>
                        </small>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="integration"
                      className="settings-tab-link"
                    >
                      <FontAwesomeIcon icon={faTelegram} size="lg" />
                      <div>
                        <strong className="d-block">Integration</strong>
                        <small>
                          <i>Manage Integration API</i>
                        </small>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="google-sheet">
                    <SheetsSettingsTab
                      oath2Token={oath2Token}
                      settings={settings}
                      setOath2Token={setOath2Token}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="telegram-report">
                    <Form>
                      <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm="3">
                          <strong>Auth Command</strong>
                        </Form.Label>
                        <Col sm="9">
                          <CopyButton
                            textToCopy={`/dreamteam auth ${currentUser?._id}`}
                          >
                            {" "}
                            <small>
                              <i>Telegram command</i>:
                            </small>{" "}
                            <code>/dreamteam auth {currentUser?._id}</code>
                          </CopyButton>
                        </Col>
                      </Form.Group>
                      <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm="3">
                          <strong>Select store for group</strong>
                        </Form.Label>
                        <Col sm="12">
                          {selectedValues.map((item, i) => {
                            const selectedData = selectedValues[i].stores;
                            const slug = selectedValues[i].slug;

                            return (
                              <div
                                key={i}
                                style={{
                                  background: "#f3f3f3",
                                  padding: "1em",
                                  borderRadius: "5px",
                                }}
                                className="mb-4"
                              >
                                <InputGroup className="mb-3">
                                  <InputGroup.Text>
                                    <small>
                                      <i>Telegram command:</i>
                                    </small>{" "}
                                    <code>/dreamteam report</code>
                                  </InputGroup.Text>
                                  <Form.Control
                                    id={`telebot.slug.${i}`}
                                    value={slug}
                                    onChange={(e) => {
                                      handleUpdateSlug(e, i);
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    onClick={(e) => {
                                      handleRemoveGroup(e, i);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </InputGroup>

                                <Autocomplete
                                  className="mb-4"
                                  multiple
                                  disabled={loading}
                                  options={storeOptions}
                                  value={selectedData}
                                  onChange={(event, newValue) => {
                                    selectOptionHandle(i, newValue);
                                  }}
                                  getOptionLabel={(option) => option.label}
                                  isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                  } // unique
                                  loading={loading}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label={`Group ${i + 1}`}
                                      placeholder="Select..."
                                    />
                                  )}
                                />
                              </div>
                            );
                          })}

                          <div className="text-end">
                            <Button
                              variant="secondary"
                              disabled={loading}
                              className="me-2"
                              onClick={() => {
                                addMoreGroup();
                              }}
                            >
                              Add more group
                            </Button>
                            <Button
                              variant="primary"
                              disabled={loading}
                              onClick={() => {
                                handleSaveTeleStoresOptions();
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </Col>
                      </Form.Group>
                    </Form>
                  </Tab.Pane>
                  <Tab.Pane eventKey="integration">
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm="3">
                        <strong>API token</strong>
                      </Form.Label>
                      <Col sm="12">
                        <InputGroup className="mb-3">
                          <InputGroup.Text>
                            <i>Bearer</i>
                          </InputGroup.Text>
                          <Form.Control type="text" readOnly value={apiToken} />
                          <Button
                            variant="primary"
                            onClick={handleApiGenerateToken}
                          >
                            <FontAwesomeIcon icon={faUnlockKeyhole} /> Generate
                          </Button>
                        </InputGroup>
                      </Col>
                    </Form.Group>
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </div>
      </div>
    </main>
  );
};

export default SettingsPage;
